/**
 * Goal Tracker / OKR Dashboard Widget - Premium Liquid Crystal Design
 * =====================================================================
 * Business objectives progress, team/individual goals,
 * and milestone celebrations. Apple-inspired glassmorphism.
 */

import React, { useState } from 'react';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Target, Trophy, TrendingUp, ChevronRight, CheckCircle2,
  Circle, Flag, Users, User, Sparkles, Clock, AlertTriangle
} from 'lucide-react';

// Industry-specific OKR data
const INDUSTRY_OKR = {
  hospitality: {
    companyGoals: [
      { id: 1, objective: 'Increase Revenue by 25%', progress: 78, status: 'on_track', dueDate: 'Q2 2026', keyResults: [
        { id: 'kr1', result: 'Achieve $2.5M quarterly revenue', progress: 82, status: 'on_track' },
        { id: 'kr2', result: 'Increase average check by 15%', progress: 95, status: 'ahead' },
        { id: 'kr3', result: 'Launch 2 new menu categories', progress: 50, status: 'on_track' },
      ], owner: 'Christian' },
      { id: 2, objective: 'Improve Customer Satisfaction to 4.8', progress: 92, status: 'ahead', dueDate: 'Q2 2026', keyResults: [
        { id: 'kr4', result: 'Achieve 90% positive reviews', progress: 95, status: 'ahead' },
        { id: 'kr5', result: 'Reduce complaint rate by 50%', progress: 88, status: 'on_track' },
      ], owner: 'Sarah' },
    ],
    teamGoals: [
      { id: 't1', team: 'Front of House', goal: 'Upsell rate 30%+', progress: 85, status: 'ahead' },
      { id: 't2', team: 'Kitchen', goal: 'Ticket time <12 min', progress: 72, status: 'on_track' },
      { id: 't3', team: 'Bar', goal: '$15K weekly sales', progress: 95, status: 'ahead' },
    ],
    milestones: [
      { id: 'm1', title: '100 5-Star Reviews', achieved: true, date: 'Feb 10', icon: Star },
      { id: 'm2', title: '$500K Revenue', achieved: true, date: 'Jan 28', icon: Trophy },
      { id: 'm3', title: 'Zero Complaints Week', achieved: false, date: 'Target: Mar 31', icon: Target },
    ],
  },
  property: {
    companyGoals: [
      { id: 1, objective: 'Achieve $50M in Sales Volume', progress: 72, status: 'on_track', dueDate: 'Q2 2026', keyResults: [
        { id: 'kr1', result: 'List 40 new properties', progress: 78, status: 'on_track' },
        { id: 'kr2', result: 'Maintain 28-day average days on market', progress: 65, status: 'at_risk' },
        { id: 'kr3', result: 'Achieve 92% auction clearance rate', progress: 88, status: 'ahead' },
      ], owner: 'Rebecca' },
      { id: 2, objective: 'Grow Rental Portfolio to 200 Properties', progress: 85, status: 'ahead', dueDate: 'Q2 2026', keyResults: [
        { id: 'kr4', result: 'Onboard 30 new landlords', progress: 90, status: 'ahead' },
        { id: 'kr5', result: 'Reduce vacancy rate to <2%', progress: 82, status: 'on_track' },
      ], owner: 'Daniel' },
    ],
    teamGoals: [
      { id: 't1', team: 'Sales Team', goal: '10 listings/month', progress: 82, status: 'on_track' },
      { id: 't2', team: 'Property Mgmt', goal: '98% occupancy', progress: 94, status: 'ahead' },
      { id: 't3', team: 'Leasing', goal: '< 21 day avg', progress: 68, status: 'at_risk' },
    ],
    milestones: [
      { id: 'm1', title: '$10M Quarter Achieved', achieved: true, date: 'Jan 15', icon: Trophy },
      { id: 'm2', title: '150th Property Under Management', achieved: true, date: 'Feb 1', icon: Star },
      { id: 'm3', title: '$100M Total Sales', achieved: false, date: 'Target: Jun 30', icon: Target },
    ],
  },
  technology: {
    companyGoals: [
      { id: 1, objective: 'Grow MRR to $150K', progress: 68, status: 'on_track', dueDate: 'Q2 2026', keyResults: [
        { id: 'kr1', result: 'Close 12 new enterprise clients', progress: 72, status: 'on_track' },
        { id: 'kr2', result: 'Reduce churn to <3% monthly', progress: 85, status: 'ahead' },
        { id: 'kr3', result: 'Launch SaaS analytics module', progress: 45, status: 'at_risk' },
      ], owner: 'Alex' },
      { id: 2, objective: 'Achieve 4.7+ Client Satisfaction', progress: 88, status: 'ahead', dueDate: 'Q2 2026', keyResults: [
        { id: 'kr4', result: '95% project on-time delivery', progress: 92, status: 'ahead' },
        { id: 'kr5', result: 'Reduce bug backlog by 60%', progress: 78, status: 'on_track' },
      ], owner: 'Priya' },
    ],
    teamGoals: [
      { id: 't1', team: 'Engineering', goal: '95% sprint completion', progress: 88, status: 'ahead' },
      { id: 't2', team: 'Design', goal: '<3 day turnaround', progress: 72, status: 'on_track' },
      { id: 't3', team: 'Sales', goal: '$50K new MRR', progress: 62, status: 'at_risk' },
    ],
    milestones: [
      { id: 'm1', title: '100th Enterprise Client', achieved: true, date: 'Jan 20', icon: Star },
      { id: 'm2', title: '$1M ARR Milestone', achieved: true, date: 'Dec 15', icon: Trophy },
      { id: 'm3', title: 'SOC 2 Certification', achieved: false, date: 'Target: Apr 30', icon: Target },
    ],
  },
  healthcare: {
    companyGoals: [
      { id: 1, objective: 'Increase Billings by 20%', progress: 75, status: 'on_track', dueDate: 'Q2 2026', keyResults: [
        { id: 'kr1', result: '85% practitioner utilization', progress: 80, status: 'on_track' },
        { id: 'kr2', result: 'Reduce no-show rate to <5%', progress: 72, status: 'on_track' },
      ], owner: 'Dr. Nguyen' },
    ],
    teamGoals: [
      { id: 't1', team: 'GPs', goal: '85% utilization', progress: 80, status: 'on_track' },
      { id: 't2', team: 'Nursing', goal: '98% care compliance', progress: 95, status: 'ahead' },
      { id: 't3', team: 'Admin', goal: '<10 min wait time', progress: 62, status: 'at_risk' },
    ],
    milestones: [
      { id: 'm1', title: '5000th Patient Registered', achieved: true, date: 'Jan 8', icon: Star },
      { id: 'm2', title: 'Telehealth Launch', achieved: true, date: 'Feb 1', icon: Trophy },
      { id: 'm3', title: 'AGPAL Accreditation', achieved: false, date: 'Target: May 15', icon: Target },
    ],
  },
};

const getOKRData = (industry) => {
  const key = industry?.toLowerCase() || 'hospitality';
  return INDUSTRY_OKR[key] || INDUSTRY_OKR.hospitality;
};

// Import Star for milestones
import { Star } from 'lucide-react';

const statusStyles = {
  ahead: { text: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', gradient: 'from-emerald-400 to-[#0066CC]', shadow: 'shadow-emerald-500/25' },
  on_track: { text: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200', gradient: 'from-blue-400 to-indigo-500', shadow: 'shadow-blue-500/25' },
  at_risk: { text: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', gradient: 'from-amber-400 to-orange-500', shadow: 'shadow-amber-500/25' },
  behind: { text: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200', gradient: 'from-red-400 to-rose-500', shadow: 'shadow-red-500/25' },
  complete: { text: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', gradient: 'from-emerald-400 to-[#0066CC]', shadow: 'shadow-emerald-500/25' },
};

const GoalTrackerWidget = ({
  industry = 'hospitality',
  className = '',
  compact = false,
  onViewDetails,
  onUpdateGoal
}) => {
  const [activeTab, setActiveTab] = useState('company');
  const [expandedGoal, setExpandedGoal] = useState(null);
  
  const { companyGoals, teamGoals, milestones } = getOKRData(industry);
  const overallProgress = Math.round(companyGoals.reduce((sum, g) => sum + g.progress, 0) / companyGoals.length);
  const achievedMilestones = milestones.filter(m => m.achieved).length;

  if (compact) {
    return (
      <div 
        className={`relative overflow-hidden rounded-3xl bg-white/70 backdrop-blur-2xl border border-white/30 shadow-[0_8px_32px_rgb(0,0,0,0.06),inset_0_0_0_1px_rgba(255,255,255,0.5)] p-5 transition-all duration-300 hover:shadow-[0_12px_40px_rgb(0,0,0,0.1)] hover:-translate-y-0.5 ${className}`} 
        data-testid="goal-tracker-compact"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-transparent pointer-events-none" />
        <div className="relative">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center shadow-lg shadow-violet-500/20">
                <Target className="w-4 h-4 text-white" />
              </div>
              <h4 className="font-semibold text-gray-900 tracking-tight">Q4 Goals</h4>
            </div>
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-violet-100 text-violet-700 border border-violet-200 whitespace-nowrap">{overallProgress}%</span>
          </div>
          <Progress value={overallProgress} className="h-2 mb-3" />
          <div className="flex justify-between text-xs text-gray-500">
            <span>{companyGoals.filter(g => g.status === 'ahead' || g.status === 'on_track').length}/{companyGoals.length} on track</span>
            <span>{achievedMilestones}/{milestones.length} milestones</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`relative overflow-hidden rounded-3xl bg-white/70 backdrop-blur-2xl border border-white/30 shadow-[0_8px_32px_rgb(0,0,0,0.06),inset_0_0_0_1px_rgba(255,255,255,0.5)] p-6 transition-all duration-300 hover:shadow-[0_12px_40px_rgb(0,0,0,0.1)] ${className}`} 
      data-testid="goal-tracker-widget"
    >
      {/* Background gradient orb */}
      <div className="absolute -top-20 -right-20 w-56 h-56 bg-gradient-to-br from-violet-100/50 to-purple-200/30 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-transparent pointer-events-none" />
      
      <div className="relative">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center shadow-lg shadow-violet-500/25">
              <Target className="w-5 h-5 text-white" />
            </div>
            <div className="flex items-center gap-2.5">
              <h3 className="font-semibold text-gray-900 tracking-tight">Goal Tracker</h3>
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-violet-50 text-violet-700 border border-violet-100 whitespace-nowrap">Q4 2025</span>
            </div>
          </div>
          <button 
            onClick={onViewDetails}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors whitespace-nowrap"
          >
            Full OKRs <ChevronRight className="w-3 h-3 ml-1" />
          </button>
        </div>

        {/* Overall Progress */}
        <div className="p-4 rounded-2xl bg-gradient-to-br from-violet-50/80 to-purple-50/50 border border-violet-100 mb-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-violet-800">Overall Progress</span>
            <span className="text-2xl font-bold text-violet-700">{overallProgress}%</span>
          </div>
          <Progress value={overallProgress} className="h-3 mb-2" />
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
            <span className="text-violet-600">
              {companyGoals.filter(g => g.status === 'ahead').length} ahead, {companyGoals.filter(g => g.status === 'on_track').length} on track
            </span>
            <span className="text-violet-600">{achievedMilestones} milestones achieved</span>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-3 h-10 mb-4 p-1 bg-gray-100/50 rounded-2xl">
            <TabsTrigger value="company" className="text-xs gap-1.5 rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <Flag className="w-3.5 h-3.5" /> Company
            </TabsTrigger>
            <TabsTrigger value="team" className="text-xs gap-1.5 rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <Users className="w-3.5 h-3.5" /> Teams
            </TabsTrigger>
            <TabsTrigger value="milestones" className="text-xs gap-1.5 rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <Trophy className="w-3.5 h-3.5" /> Milestones
            </TabsTrigger>
          </TabsList>

          {/* Company Goals Tab */}
          <TabsContent value="company" className="space-y-3 mt-0">
            {companyGoals.map(goal => {
              const style = statusStyles[goal.status];
              const isExpanded = expandedGoal === goal.id;
              
              return (
                <div 
                  key={goal.id} 
                  className={`p-4 rounded-2xl border cursor-pointer transition-all hover:shadow-lg hover:-translate-y-0.5 ${style.bg} ${style.border}`}
                  onClick={() => setExpandedGoal(isExpanded ? null : goal.id)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col gap-1.5 mb-2">
                        <h4 className="font-semibold text-gray-900 text-sm leading-snug">{goal.objective}</h4>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${style.bg} ${style.text} border ${style.border} whitespace-nowrap`}>
                            {goal.status.replace('_', ' ')}
                          </span>
                          <p className="text-xs text-gray-500">Owner: {goal.owner} • Due: {goal.dueDate}</p>
                        </div>
                      </div>
                    </div>
                    <span className="text-xl font-bold text-gray-900 shrink-0">{goal.progress}%</span>
                  </div>
                  <Progress value={goal.progress} className="h-2" />
                  
                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t border-gray-200/50 space-y-2.5">
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Key Results</p>
                      {goal.keyResults.map(kr => {
                        const krStyle = statusStyles[kr.status];
                        return (
                          <div key={kr.id} className="flex items-center gap-3 p-2 rounded-xl bg-white/60">
                            {kr.status === 'complete' ? (
                              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                            ) : (
                              <Circle className="w-5 h-5 text-gray-300" />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-gray-700">{kr.result}</p>
                              <Progress value={kr.progress} className="h-1.5 mt-1" />
                            </div>
                            <span className={`text-sm font-bold ${krStyle.text}`}>{kr.progress}%</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </TabsContent>

          {/* Team Goals Tab */}
          <TabsContent value="team" className="space-y-3 mt-0">
            {teamGoals.map((goal, idx) => {
              const style = statusStyles[goal.status];
              return (
                <div key={goal.id} className={`flex items-center gap-3 p-4 rounded-2xl border transition-all hover:shadow-md hover:-translate-y-0.5 ${style.bg} ${style.border}`}>
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${style.gradient} flex items-center justify-center shadow-lg ${style.shadow}`}>
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-gray-900 text-sm">{goal.team}</span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${style.bg} ${style.text} border ${style.border} whitespace-nowrap`}>
                        {goal.status.replace('_', ' ')}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">{goal.goal}</p>
                    <Progress value={goal.progress} className="h-1.5 mt-2" />
                  </div>
                  <span className="text-xl font-bold text-gray-900">{goal.progress}%</span>
                </div>
              );
            })}
          </TabsContent>

          {/* Milestones Tab */}
          <TabsContent value="milestones" className="space-y-3 mt-0">
            {milestones.map(milestone => {
              const MilestoneIcon = milestone.icon;
              return (
                <div 
                  key={milestone.id} 
                  className={`flex items-center gap-4 p-4 rounded-2xl border transition-all hover:shadow-md hover:-translate-y-0.5 ${
                    milestone.achieved 
                      ? 'bg-gradient-to-r from-amber-50/80 to-yellow-50/50 border-amber-200' 
                      : 'bg-gradient-to-r from-gray-50/80 to-white/50 border-gray-100'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg ${
                    milestone.achieved
                      ? 'bg-gradient-to-br from-amber-400 to-yellow-500 shadow-amber-500/25'
                      : 'bg-gradient-to-br from-gray-300 to-gray-400 shadow-gray-400/25'
                  }`}>
                    <MilestoneIcon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className={`font-semibold text-sm ${milestone.achieved ? 'text-amber-800' : 'text-gray-700'}`}>
                      {milestone.title}
                    </p>
                    <p className={`text-xs ${milestone.achieved ? 'text-amber-600' : 'text-gray-500'}`}>
                      {milestone.date}
                    </p>
                  </div>
                  {milestone.achieved ? (
                    <CheckCircle2 className="w-6 h-6 text-amber-500" />
                  ) : (
                    <Clock className="w-6 h-6 text-gray-400" />
                  )}
                </div>
              );
            })}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default GoalTrackerWidget;
